<template>
	<article data-component="accordion" :data-open="open">
		<h3 class="accordion-heading">
			<button :id="triggerId" class="accordion-trigger" type="button" :aria-expanded="open" :aria-controls="panelId" @click="emit('toggle')">
				<span class="trigger-label">
					<span class="-caption-medium number">{{ number }}</span>
					<span class="-headline-5 question">{{ title }}</span>
				</span>
				<span class="toggle-icon" aria-hidden="true">
					<Icon slug="ic-plus" size="sm" />
				</span>
			</button>
		</h3>

		<div :id="panelId" class="accordion-panel" role="region" :aria-labelledby="triggerId" :aria-hidden="!open">
			<div class="panel-inner">
				<p v-if="text" class="-body-regular answer">{{ text }}</p>
			</div>
		</div>
	</article>
</template>

<script setup>
import { Icon } from '~/components/core'

const props = defineProps({
	id: { type: String, required: true },
	number: { type: String, required: true },
	title: { type: String, required: true },
	text: { type: String, default: '' },
	open: { type: Boolean, default: false },
})

const emit = defineEmits(['toggle'])

const triggerId = computed(() => `${props.id}-trigger`)
const panelId = computed(() => `${props.id}-panel`)
</script>

<style lang="scss" scoped>
[data-component='accordion'] {
	--accordion-duration: 0.7s;
	--accordion-index-width: 20px;
	--accordion-column-gap: 10px;

	position: relative;
	border-top: 1px solid var(--color-river-blue-100);

	&::before {
		content: '';
		position: absolute;
		top: -1px;
		left: 0;
		width: 100%;
		height: 1px;
		background-color: var(--color-river-blue-500);
		transform: scaleX(0);
		transform-origin: left center;
		transition: transform var(--accordion-duration) ease(out-quint), background-color var(--accordion-duration) ease(out-quint);
		pointer-events: none;
	}

	.accordion-heading {
		margin: 0;
		min-height: 86px;
		transition: min-height var(--accordion-duration) ease(out-quint);
	}

	.accordion-trigger {
		display: grid;
		grid-template-columns: minmax(0, 1fr) rs(44, 44);
		gap: rs(10, 10);
		align-items: flex-start;
		width: 100%;
		padding: rs(12, 10) 0 rs(10, 10);
		text-align: left;
		cursor: pointer;

		&:focus-visible {
			outline: 2px solid var(--color-river-blue-500);
			outline-offset: 4px;
		}
	}

	.trigger-label {
		display: grid;
		grid-template-columns: var(--accordion-index-width) minmax(0, 1fr);
		gap: var(--accordion-column-gap);
		align-items: baseline;
		//
	}

	.number {
		color: var(--color-river-blue-500);
	}

	.question {
		@include headline-5;
		color: var(--color-river-blue-900);
	}

	.toggle-icon {
		--icon-color: var(--color-river-blue-500);

		display: flex;
		align-items: center;
		justify-content: center;
		width: rs(44, 44);
		height: rs(44, 44);
		border-radius: rs(12, 10);
		background-color: var(--color-river-blue-100);
		transition: background-color var(--accordion-duration) ease(out-quint);

		:deep([data-component='icon']) {
			width: rs(16, 16);
			height: rs(16, 16);
			transition: transform var(--accordion-duration) ease(out-quint);
		}
	}

	@media (hover: hover) {
		&[data-open='false']:hover::before {
			background-color: var(--color-river-blue-300);
			transform: scaleX(0.3);
		}

		&[data-open='false'] .accordion-trigger:hover .toggle-icon {
			--icon-color: var(--color-geral-white);
			background-color: var(--color-river-blue-300);
		}
	}

	.accordion-panel {
		margin: rs(-15, -15) 0 0;
		display: grid;
		grid-template-rows: 0fr;
		visibility: hidden;
		transition: grid-template-rows var(--accordion-duration) ease(out-quint), visibility 0s linear var(--accordion-duration);
	}

	.panel-inner {
		overflow: hidden;
	}

	.answer {
		@include body-regular;
		padding: 0 rs(66, 54) rs(30, 24) calc(var(--accordion-index-width) + var(--accordion-column-gap));
		padding: 0 calc(column-width(1, true)) rs(30, 24) calc(var(--accordion-index-width) + var(--accordion-column-gap));

		color: var(--color-neutral-tints-700);
		opacity: 0;
		white-space: pre-line;
		transition: opacity var(--accordion-duration) ease(out-quint);
	}

	&[data-open='true'] {
		.accordion-heading {
			min-height: rs(66, 64);
		}

		&::before {
			transform: scaleX(1);
		}

		.toggle-icon {
			--icon-color: var(--color-geral-white);
			background-color: var(--color-river-blue-500);

			:deep([data-component='icon']) {
				transform: rotate(45deg);
			}
		}

		.accordion-panel {
			grid-template-rows: 1fr;
			visibility: visible;
			transition: grid-template-rows var(--accordion-duration) ease(out-quint), visibility 0s linear 0s;
		}

		.answer {
			opacity: 1;
		}
	}
}
</style>
